import axios from 'axios';

const OWNER = { base: `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting`, headers: {} };
const KEY = 'sb_upload_jobs';
const MAX_AGE = 15 * 60 * 1000;

// Jobs are remembered so an upload keeps going (and its results come back) even if the phone switches apps or reloads the tab.
export const savedJobs = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]').filter((j) => Date.now() - j.at < MAX_AGE); } catch { return []; }
};
export const rememberJob = (job) => localStorage.setItem(KEY, JSON.stringify([...savedJobs().filter((j) => j.id !== job.id), { ...job, at: Date.now() }]));
export const forgetJob = (id) => localStorage.setItem(KEY, JSON.stringify(savedJobs().filter((j) => j.id !== id)));
export const forgetAllJobs = () => localStorage.removeItem(KEY);

const aborted = () => Object.assign(new Error('Stopped'), { stopped: true });

// Sleep that wakes early when the page comes back into view, and stops on abort.
const nap = (ms, signal) => new Promise((resolve, reject) => {
  if (signal?.aborted) { reject(aborted()); return; }
  const done = () => { clearTimeout(t); document.removeEventListener('visibilitychange', onVis); signal?.removeEventListener('abort', onAbort); };
  const onVis = () => { if (!document.hidden) { done(); resolve(); } };
  const onAbort = () => { done(); reject(aborted()); };
  const t = setTimeout(() => { done(); resolve(); }, ms);
  document.addEventListener('visibilitychange', onVis);
  signal?.addEventListener('abort', onAbort);
});

const untilVisible = (signal) => (document.hidden ? nap(10 * 60 * 1000, signal) : Promise.resolve());

// Send the files; if the phone dropped the connection while in another app, resend once it's back.
export async function postUpload(fd, signal, api = OWNER) {
  for (let attempt = 0; ; attempt++) {
    try {
      return (await axios.post(`${api.base}/upload`, fd, { signal, headers: { ...api.headers, 'Content-Type': 'multipart/form-data' } })).data;
    } catch (e) {
      if (signal?.aborted || axios.isCancel(e)) throw aborted();
      if (e.response || attempt >= 3) throw e;  // a real answer from the server (e.g. daily limit) is final
      await untilVisible(signal);
      await nap(1500, signal);
    }
  }
}

// Wait for the server to finish reading (it keeps working even while this page is in the background).
export async function pollUploadJob(jobId, signal, api = OWNER) {
  const deadline = Date.now() + 12 * 60 * 1000;
  let misses = 0;
  while (Date.now() < deadline) {
    await nap(3000, signal);
    let data;
    try {
      ({ data } = await axios.get(`${api.base}/upload-jobs/${jobId}`, { signal, headers: api.headers }));
      misses = 0;
    } catch (e) {
      if (signal?.aborted || axios.isCancel(e)) throw aborted();
      if (e.response?.status === 404) throw e;
      if (!document.hidden && ++misses > 20) throw e;  // errors while in another app don't count
      continue;
    }
    if (data.status === 'done') return data.result || {};
    if (data.status === 'cancelled') throw aborted();
    if (data.status === 'error') throw new Error(data.error || 'Video could not be read');
  }
  throw new Error('Video took too long to read — try a shorter recording');
}

export const cancelJob = (id, api = OWNER) => axios.post(`${api.base}/upload-jobs/${id}/cancel`, null, { headers: api.headers }).catch(() => {});

// Keep the screen awake while uploading so the phone doesn't lock mid-upload.
export async function keepAwake() {
  try { return await navigator.wakeLock?.request('screen'); } catch { return null; }
}
